import { Button, Code, Modal, Stack, Title } from '@mantine/core';
import React from 'react';
import { SimpleTable } from 'src/common/components/table/SimpleTable';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import { UnprocessedValuation } from './types';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { useAddTestValuationUpdate } from 'src/secret-menu/bulk-valuations/hooks/useAddTestValuationUpdate';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';

type ProviderPair = {
  productProvider: string;
  valuationsProvider: string;
};

export default function UnprocessedValuationUpdatesTab() {
  return <CompanyWrapper contentComponent={ValuationsContent} />;
}

function ProviderPairsTable({ tenantName }: { tenantName: string }) {
  const columns = [
    {
      accessor: 'productProvider',
      title: 'Product Provider',
    },
    {
      accessor: 'valuationsProvider',
      title: 'Valuations Platform',
    },
  ];

  return (
    <SimpleTable<ProviderPair>
      mode="client"
      resource={`admin/companies/${tenantName}/valuation-provider-pairs`}
      columns={columns}
      showCount={false}
      enableSearch={false}
      pageSize={100}
    />
  );
}

function UnprocessedValuationsTable({ company }: { company: Company }) {
  const [selectedValuation, setSelectedValuation] = React.useState<UnprocessedValuation | null>(
    null,
  );

  const resource = `admin/companies/${company.id}/unprocessed-valuation-updates`;

  const columns = [
    {
      accessor: 'id',
      title: 'ID',
    },
    {
      accessor: 'providerReferenceNumber',
      title: 'Provider reference number',
    },
  ];

  return (
    <>
      <Modal
        opened={!!selectedValuation}
        onClose={() => setSelectedValuation(null)}
        title="Valuation content"
        size="xxl"
      >
        <Code block>{JSON.stringify(selectedValuation?.updateContent, null, 2)}</Code>
      </Modal>

      <SimpleTable<UnprocessedValuation>
        mode="server"
        resource={resource}
        title="Valuations"
        columns={columns}
        onRowClick={setSelectedValuation}
        showCount={false}
      />
    </>
  );
}

function ValuationsContent({ company }: { company: Company }) {
  const { addTestValuationUpdate, isLoading } = useAddTestValuationUpdate();

  const handleAddTestValuation = async () => {
    try {
      await addTestValuationUpdate();
      window.location.reload();
    } catch (error) {
      notifyAndLogError(
        'Error adding test valuation',
        'There was an error adding the test valuation. Please try again.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      <div>
        <Title order={4} mb="sm">Provider Pairs</Title>
        <ProviderPairsTable tenantName={company.tenantName} />
      </div>

      <UnprocessedValuationsTable company={company} />

      <Button loading={isLoading} onClick={handleAddTestValuation}>
        Add test valuation
      </Button>
    </Stack>
  );
}
